<template>
  <div class="container">
    <div class="mt-3 mb-5">
      <label
        for="date-search"
        class="mr-3"
      >{{ $t('appointmentPage.appointments.searchByDate') }}</label>
      <Calendar
        id="date-search"
        v-model="selectedDate"
        @date-select="searchByDate"
      />
    </div>

    <FullCalendar
      ref="roamingTeamCalendar"
      :options="calendarOptions"
    >
      <template #eventContent="arg">
        <strong>{{ arg.timeText }}</strong>
        <Button
          v-if="arg.event.display !== 'background'"
          icon="pi pi-times"
          :title="$t('appointmentPage.appointments.removeAppointment')"
          class="p-button-rounded p-button-secondary"
          @click="removeAppointment(arg.event.id)"
        />
      </template>
    </FullCalendar>

    <Dialog
      id="appointment-dialog"
      v-model:visible="diplayAppointmentDialog"
      :breakpoints="{'1600px': '60vw', '960px': '75vw', '640px': '90vw'}"
    >
      <template #header>
        <h3>{{ $t('appointmentPage.appointments.editDialogHeader') }}</h3>
      </template>

      <div class="grid field">
        <label
          for="day"
          class="col-12 mb-2 md:col-5 md:mb-0"
        >{{ $t('entity.appointment.day') }}</label>
        <div class="col-12 md:col-6">
          <Calendar
            id="day"
            v-model="v$.appointment.day.$model"
            disabled
          />
        </div>
      </div>

      <div class="grid field">
        <label
          for="start"
          class="col-12 mb-2 md:col-5 md:mb-0 required-input"
        >{{ $t('entity.appointment.start') }}</label>
        <div class="col-12 md:col-6">
          <Calendar
            id="start"
            v-model="v$.appointment.start.$model"
            :time-only="true"
            hour-format="24"
            :class="{'invalid-input': v$.appointment.start.$error }"
          />
          <div
            v-for="error of v$.appointment.start.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <div class="grid field">
        <label
          for="end"
          class="col-12 mb-2 md:col-5 md:mb-0 required-input"
        >{{ $t('entity.appointment.end') }}</label>
        <div class="col-12 md:col-6">
          <Calendar
            id="end"
            v-model="v$.appointment.end.$model"
            :time-only="true"
            hour-format="24"
            :class="{'invalid-input': v$.appointment.end.$error }"
          />
          <div
            v-for="error of v$.appointment.end.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <Message
        v-if="appointmentErrors.length > 0"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of appointmentErrors"
            :key="error"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>

      <template #footer>
        <Button
          :label="$t('action.back')"
          class="p-button-rounded p-button-outlined p-button-secondary"
          @click="closeAppointmentDialog"
        />
        <Button
          :disabled="v$.$invalid"
          :label="$t('action.confirm')"
          type="submit"
          class="p-button-rounded p-button-outlined"
          @click="validateAppointment(appointment)"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import '@fullcalendar/core/vdom'
import FullCalendar from '@fullcalendar/vue3'
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction'
import {useVuelidate} from "@vuelidate/core";
import {required} from "@/i18n/i18nValidators";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import frLocale from '@fullcalendar/core/locales/fr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import {v4 as uuidv4} from 'uuid';
import {
  appointmentInBusinessHours,
  checkNoAppointmentOverlap,
  getAppointmentDuration,
  getTotalAppointmentsDuration
} from "@/shared/utils/appointmentSlotUtils";
import Message from "primevue/message";
import moment from "moment";
import {reactive} from "vue";
import useAppointmentFilter from "@/composables/filters/useAppointmentFilter";
import useAppointmentStatus from "@/composables/useAppointmentStatus";

export default {
  name: 'RoamingTeamCalendar',
  components: { FullCalendar, Dialog, Button, Calendar, Message },
  props: {
    roamingTeam: {
      type: Object,
      required: true
    },
    appointments: {
      type: Array,
      required: true
    },
    controlDuration: {
      type: Number,
      required: true
    }
  },
  emits: ['createAppointment', 'removeAppointment'],
  setup() {
    const appointmentFilter = reactive(useAppointmentFilter());
    const appointmentStatus = useAppointmentStatus()
    appointmentFilter.pageable.size = 1000;
    appointmentFilter.filters.status.value = [appointmentStatus.NO_SHOW, appointmentStatus.HONORED];
    return {
      v$: useVuelidate(),
      appointmentFilter
    }
  },
  data() {
    return {
      calendarOptions: {
        headerToolbar: {
          left: '',
          center: 'title',
          right: 'today prev,next'
        },
        plugins: [timeGridPlugin, interactionPlugin],
        initialView: 'timeGridWeek',
        allDaySlot: false,
        locales: [enLocale, frLocale],
        locale: this.$i18n.locale,
        slotMinTime: '06:00',
        slotMaxTime: '22:00',
        scrollTimeReset: false,
        businessHours: [],
        events: async ( fetchInfo, successCallback, _ ) => {
          this.appointmentFilter.filters.start.value = new Date(fetchInfo.start);
          this.appointmentFilter.filters.end.value = new Date(fetchInfo.end);
          const events = await this.fetchUnavailable()
          successCallback(events)
        },
        eventOverlap: false,
        selectConstraint: 'businessHours',
        slotDuration: '00:20:00',
        selectable: true,
        selectMirror: true,
        selectOverlap: false,
        dateClick: this.dateClick,
        select: this.appointmentSelection,
      },
      diplayAppointmentDialog: false,
      appointmentErrors: [],
      unavailableAppointments: [],
      appointment: {

      },
      selectedDate: new Date(),
      errorAppointmentOverlap: 'validations.appointment.overlap',
      errorAppointmentExceedsControlDuration: 'validations.appointment.exceedsControlDuration',
      errorAppointmentGreaterThan0: 'validations.appointment.greaterThan0',
      windowWidth: window.innerWidth,
    }
  },
  validations() {
    return {
      appointment: {
        day: {},
        start: { required },
        end: {
          required,
        }
      }
    }
  },
  created() {
    window.addEventListener('resize', this.setCalendarView)
    this.$watch('$i18n.locale', (newVal) => this.calendarOptions.locale = newVal)
    this.$watch('appointments', () => {
      this.reloadUnavailable()
    })
    this.$emitter.on('book-failed', () => this.reloadUnavailable())
  },
  mounted() {
    this.initCalendarData()
  },
  unmounted() {
    window.removeEventListener('resize', this.setCalendarView)
  },
  methods: {
    initCalendarData() {
      this.getRoamingTeamPlanning()
      this.setCalendarView()
    },

    reloadUnavailable() {
      if (this.$refs.roamingTeamCalendar) {
        this.$refs.roamingTeamCalendar.getApi().refetchEvents()
      }
    },

    async fetchUnavailable(){
      const unavailable = []
      this.appointmentFilter.filters.roamingTeamId.value = this.roamingTeam.id

      const {content} = await this.$appointmentService.getAll(
        this.appointmentFilter.getQueryParams())

      const appointmentsByTeam = content;
      if (appointmentsByTeam && appointmentsByTeam.length !== 0) {
        this.unavailableAppointments = appointmentsByTeam;

        appointmentsByTeam.forEach((appointment) => {
          unavailable.push({
            id: appointment.id,
            start: new Date(appointment.start),
            end: new Date(appointment.start.split('T')[0] + 'T' + appointment.end),
            allDay: false,
            display: 'background',
            overlap: 'false'
          })
        })
      }

      let availabilityStartDate = new Date(this.roamingTeam.availabilityStartDate)
      unavailable.push({
        start: new Date(availabilityStartDate.setFullYear(availabilityStartDate.getFullYear() - 5)),
        end: this.roamingTeam.availabilityStartDate,
        allDay: true,
        display: 'background',
        overlap: 'false'
      })

      let availabilityEndDate = new Date(this.roamingTeam.availabilityEndDate)
      unavailable.push({
        start: this.roamingTeam.availabilityEndDate,
        end: new Date(availabilityEndDate.setFullYear(availabilityEndDate.getFullYear() + 5)),
        allDay: true,
        display: 'background',
        overlap: 'false'
      })

      unavailable.push(...this.appointments)
      return unavailable
    },

    remainingMinToSchedule() {
      return this.controlDuration - getTotalAppointmentsDuration(this.appointments)
    },

    async getRoamingTeamPlanning() {
      const {planning} = await this.$roamingTeamService.fetchById(this.roamingTeam.id);
      const dayPlannings = [
        planning.sundayPlanning,
        planning.mondayPlanning,
        planning.tuesdayPlanning,
        planning.wednesdayPlanning,
        planning.thursdayPlanning,
        planning.fridayPlanning,
        planning.saturdayPlanning,
      ];
      let businessHours = []
      let i = 0
      for (const dayPlanning of dayPlannings) {
        if (dayPlanning !== null) {
          if (dayPlanning.availabilityStartTime !== null && dayPlanning.availabilityEndTime !== null) {
            businessHours.push({
              daysOfWeek: [i],
              startTime: dayPlanning.availabilityStartTime.slice(0, -2),
              endTime: dayPlanning.availabilityEndTime.slice(0, -2)
            })
          }
        } else {
          businessHours.push({
            daysOfWeek: [i],
            startTime: "00:00",
            endTime: "00:00"
          })
        }
        i++
      }

      this.calendarOptions.businessHours = businessHours
    },

    searchByDate(value) {
      let fullCalendarApi = this.$refs.roamingTeamCalendar.getApi();
      fullCalendarApi.gotoDate(value)
    },

    dateClick(arg) {
      let isFutureDate = moment(new Date(arg.dateStr)).isAfter(new Date(), 'day')
      if (isFutureDate) {
        let appointment = {
          start: new Date(arg.dateStr),
        }
        this.openAppointmentDialog(appointment)
      }
    },
    appointmentSelection(arg) {
      let appointment = {
        start: arg.start,
        end: arg.end
      }
      this.openAppointmentDialog(appointment)
    },

    validateAppointment(appointment) {
      this.appointmentErrors = []

      let newAppointment = JSON.parse(JSON.stringify(appointment))
      newAppointment.day = new Date(newAppointment.day)
      newAppointment.start = new Date(newAppointment.start)
      newAppointment.end = new Date(newAppointment.end)

      let appointmentStart = new Date(newAppointment.day.toISOString().split('T')[0] + 'T' + newAppointment.start.toISOString().split('T')[1]);
      appointmentStart.setSeconds(0, 0)
      let appointmentEnd = new Date(newAppointment.day.toISOString().split('T')[0] + 'T' + newAppointment.end.toISOString().split('T')[1]);
      appointmentEnd.setSeconds(0, 0)

      let appointmentToCreate = {
        day: newAppointment.day,
        start: appointmentStart.toISOString(),
        end: appointmentEnd.toISOString(),
        roamingTeam: this.roamingTeam
      }

      let appointmentDuration = getAppointmentDuration(appointmentToCreate)
      if (appointmentDuration <= 0) {
        this.appointmentErrors.push(this.errorAppointmentGreaterThan0)
      }

      if (appointmentDuration > this.controlDuration) {
        this.appointmentErrors.push(this.errorAppointmentExceedsControlDuration)
      }

      if (!checkNoAppointmentOverlap(appointmentToCreate, this.unavailableAppointments) ||
        !appointmentInBusinessHours(appointmentToCreate, this.calendarOptions.businessHours)) {
        this.appointmentErrors.push(this.errorAppointmentOverlap)
      }

      if (this.appointmentErrors.length === 0) {
        this.addAppointment(appointmentToCreate)
      }
    },

    addAppointment(appointment) {
      this.appointmentErrors = []
      appointment.id = uuidv4()
      this.$emit('createAppointment', appointment)
      this.appointment = {}
      this.diplayAppointmentDialog = false

      this.$refs.roamingTeamCalendar.getApi().refetchEvents()
    },

    removeAppointment(appointmentId) {
      const appointment = this.appointments.find(s => s.id === appointmentId)
      this.$emit('removeAppointment', appointment)
      this.$refs.roamingTeamCalendar.getApi().refetchEvents()
    },

    openAppointmentDialog(appointment) {
      this.appointmentErrors = []
      this.diplayAppointmentDialog = true

      this.appointment = { ...appointment }
      this.appointment.day = appointment.start
      const remainingMinToSchedule = this.remainingMinToSchedule()
      this.appointment.end = moment(appointment.start).add(remainingMinToSchedule, 'minutes').toDate()
    },
    closeAppointmentDialog() {
      this.diplayAppointmentDialog = false
    },
    setCalendarView() {
      const mobileBreakpoint = 576;
      this.windowWidth = window.innerWidth;
      let fullCalendarApi = this.$refs.roamingTeamCalendar.getApi();
      if (this.windowWidth < mobileBreakpoint) {
        fullCalendarApi.changeView('timeGridDay');
      } else {
        fullCalendarApi.changeView('timeGridWeek');
      }
    }
  }
}
</script>

<style lang="scss" scoped>

h3 {
  margin: 0;
}

</style>
